import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { WalletValue, walletAmount, useAuth } from '../context/AuthContext';
import { useChatLayout } from '../context/ChatLayoutContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Gamepad2, Activity, Settings, Flame, LogIn, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { Mm2Logo } from '../components/mm2/Mm2Brand';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// ==========================================
// 🍎 ŞEFFAF (ALPHA KANALLI) WEBM DESTEK TESPİTİ
// ==========================================
// /openChest.webm alpha kanallı bir videodur (PNG gibi arka planı şeffaf).
// Apple'ın WebKit render hattı (Safari + macOS/iOS'taki TÜM tarayıcılar) VP8/VP9
// alpha kanalını çizmez → video şeffaf yerine SİYAH bir kutu olarak görünür.
// PNG karşılığı olmadığı için fallback de yok.
// Çözüm: Apple platformlarında video hiç MOUNT EDİLMEZ (gizlemek değil, hiç
// oluşturmamak → siyah kutunun bir kare bile görünme ihtimali kalmaz + boşuna
// video indirilmez).
//
// ⚠️ Bu SADECE alpha kanallı .webm için geçerlidir. Oyun kartlarındaki .mp4
//    videolar bu kontrolden ETKİLENMEZ — onlar hiçbir platform tespiti
//    kullanmaz (bkz. GameCard).
//
// ⚙️  Sadece Safari'de gizlemek istersen: aşağıdaki `isApple` satırını
//     `const isApple = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua);`
//     ile değiştirmen yeterli.

let __alphaVideoSupport = null;
const supportsAlphaVideo = () => {
  if (__alphaVideoSupport !== null) return __alphaVideoSupport;
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return (__alphaVideoSupport = false);

  const ua = navigator.userAgent || '';
  const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || '';
  // MacBook / iMac / Mac mini / iPhone / iPad (iPadOS 13+ kendini "MacIntel" olarak tanıtır)
  const isApple = /Mac|iPhone|iPad|iPod/i.test(`${platform} ${ua}`);
  if (isApple) return (__alphaVideoSupport = false);

  // Apple dışı: WebM oynatabiliyor mu? (çok eski/kısıtlı tarayıcılarda boş kutu çıkmasın)
  try {
    const probe = document.createElement('video');
    const can = probe.canPlayType('video/webm; codecs="vp9"') || probe.canPlayType('video/webm; codecs="vp8"');
    __alphaVideoSupport = can === 'probably' || can === 'maybe';
  } catch (e) {
    __alphaVideoSupport = false;
  }
  return __alphaVideoSupport;
};

const formatCompact = (number) => {
  if (!number) return "0";
  return new Intl.NumberFormat('en-US', { notation: "compact", maximumFractionDigits: 2 }).format(number);
};

// ==========================================
// ALT-ORTA YEŞİL ÇİZGİ + IŞIMA (referanstaki yeşil parlama)
// ==========================================
const BottomGreenGlow = ({ className = "" }) => (
  <div className={`absolute inset-x-0 bottom-0 flex justify-center pointer-events-none ${className}`}>
    <div className="relative w-3/5 flex justify-center">
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[200%] h-14 sm:h-16"
        style={{ background: 'radial-gradient(ellipse at bottom, rgba(22,163,74,0.55) 0%, transparent 72%)' }}
      />
      <div className="absolute bottom-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[#16A34A] to-transparent shadow-[0_0_14px_rgba(22,163,74,0.95)]" />
    </div>
  </div>
);

// ==========================================
// PREMIUM OYUN KARTI (VIDEO TABANLI)
// ==========================================
const GameCard = ({ name, provider, isNew, isHot, path, video, shape, tagline }) => {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);

  /* ⚠️ VIDEO KAYNAĞI EKRANA GİRENE KADAR BAĞLANMAZ.
     Önceden 12 kartın hepsi `preload="metadata"` ile mount ediliyordu:
     sayfa açılır açılmaz 12 ayrı istek gidiyor ve her biri videonun
     başlık bloğunu indiriyordu (~1-2 MB), üstelik videolar YALNIZCA
     hover'da oynatılıyor. Ekranın altındaki kartlar için bu tamamen
     boşa giden bir yükleme. IntersectionObserver ile kaynak, kart
     görünür alana yaklaştığında bağlanır. */
  const [srcReady, setSrcReady] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || srcReady) return;
    /* Tarayıcı desteklemiyorsa (çok eski) davranışı bozmadan yükle. */
    if (typeof IntersectionObserver === 'undefined') { setSrcReady(true); return; }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSrcReady(true);
          io.disconnect();
        }
      },
      /* 200px önceden başlat: kullanıcı kaydırırken kart hazır olsun. */
      { rootMargin: '200px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [srcReady]);

  const handleEnter = () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      /* ⚠️ readyState 0 iken currentTime atamak bazı tarayıcılarda hata
         fırlatır ve play() hiç çağrılmazdı. Metadata yoksa doğrudan
         oynat; tarayıcı zaten baştan başlatır. */
      if (v.readyState > 0) v.currentTime = 0;
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (e) {}
  };

  const handleLeave = () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.pause();
      v.currentTime = 0;
    } catch (e) {}
  };

  const handleEnded = () => {
    const v = videoRef.current;
    if (!v) return;
    try { v.currentTime = 0; } catch (e) {}
  };

  const cardContent = (
    <motion.div
      ref={wrapRef}
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="relative group cursor-pointer h-full w-full"
    >
      {/* ⚠️⚠️ SLOGAN KARTIN DIŞINDA.
          Önce sağ üst köşeye çapraz kurdele koymuştum: 45° döndürülmüş
          9px yazı okunmuyordu ve dar kartlarda taşıyordu.
          Bu sekme kartın ÜST KENARINI kesiyor — ızgaranın ritmini bozduğu
          için göz doğrudan buraya gidiyor. Yatay yazı, tam kontrast,
          okunabilir.
          ⚠️ Dış sarmalayıcıda duruyor: iç kapta `overflow-hidden` var,
          orada olsaydı taşan yarısı kırpılırdı.
          ⚠️ METİN KIRILIMA GÖRE DEĞİŞİYOR. Dikey kart mobilde ~94px,
          lg'de ~142px, xl'de ~207px. Tek bir metin hepsine sığmıyor:
          ölçtüm, "PLAY WITH MM2 ITEMS" 9px'te 124px yer kaplıyor ve
          lg'de taşıyor. Dar ekranlarda kısa sürüm gösteriliyor. */}
      {tagline && (
        /* ⚠️ `bottom-full`: sekme kartın TAMAMEN ÜSTÜNDE duruyor, içine hiç
           girmiyor. Önce `-top-2` ile yarısı içerideydi ve sol üstteki HOT
           rozetiyle çakışıyordu — üç kırılımda da ölçtüm, üçünde de üst üste
           biniyordu. Dışarıda durunca çakışma matematiksel olarak imkânsız.
           ⚠️ Izgaraya üstten boşluk eklendi, yoksa sekme başlığa girerdi. */
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-40 pointer-events-none">
          <div
            className="rounded-full whitespace-nowrap font-black text-white uppercase
                       px-2.5 py-1 sm:px-3 sm:py-1.5
                       text-[8px] sm:text-[10px] xl:text-[11px]
                       border border-white/30"
            style={{
              background: 'linear-gradient(135deg,#D08CF5 0%,#B059E8 50%,#8B3FC4 100%)',
              /* Mor ışıma + koyu halka: hem video hem sayfa zemini üzerinde
                 net ayrışıyor. Halka olmasa açık karelerde kayboluyor. */
              boxShadow: '0 6px 20px -4px rgba(176,89,232,.85), 0 0 0 3px rgba(14,15,17,.92)',
              letterSpacing: '.06em',
              textShadow: '0 1px 2px rgba(0,0,0,.35)',
            }}
          >
            <span className="xl:hidden">{tagline.short}</span>
            <span className="hidden xl:inline">{tagline.full}</span>
          </div>
        </div>
      )}
      <div className="relative h-full w-full rounded-xl overflow-hidden bg-[#1A1D20] border border-[#2A2D31] group-hover:border-[#B059E8]/60 transition-all duration-300 shadow-lg group-hover:shadow-[0_0_25px_rgba(176,89,232,0.18)]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#15171a] to-[#15171a]" />
        {video && (
          <video
            ref={videoRef}
            /* Kaynak görünür alana girene kadar BOŞ; boş src istek üretmez. */
            src={srcReady ? `${video}#t=0.001` : undefined}
            muted
            playsInline
            /* Kaynak yalnızca kart görünürken bağlandığı için "metadata"
               burada güvenli: ekran dışındaki kartlar HİÇ istek üretmez,
               görünenler ise hover'da anında oynar. "none" seçilseydi ilk
               hover'da video yüklenene kadar boş kutu görünürdü. */
            preload="metadata"
            onEnded={handleEnded}
            className="relative z-[1] w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        )}
        <div className="absolute top-2.5 left-2.5 flex gap-1 z-30">
            {isNew && (
            <span className="px-2 py-0.5 bg-[#16A34A] border border-[#16A34A]/60 text-white text-[9px] font-black tracking-wider rounded-md shadow-md">
                NEW
            </span>
            )}
            {isHot && (
            <span className="px-2 py-0.5 bg-gradient-to-r from-orange-500/90 to-red-500/90 border border-orange-400/50 text-white text-[9px] font-black tracking-wider rounded-md shadow-md flex items-center gap-1">
                <Flame className="w-2.5 h-2.5" /> HOT
            </span>
            )}
        </div>
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#08090b] via-[#08090b]/80 to-transparent pointer-events-none z-10" />
        <BottomGreenGlow className="z-[15]" />
        <div className={`absolute inset-x-0 ${shape === 'square' ? 'bottom-2.5' : 'bottom-4'} text-center z-20 pointer-events-none px-2 transform group-hover:-translate-y-1 transition-transform duration-300`}>
          <h4 className="font-heading font-black text-white uppercase tracking-wider drop-shadow-lg text-[clamp(0.7rem,1.5vw,1.1rem)] w-full px-1">
            {name}
          </h4>
          {provider && (
            <p className="text-[8px] md:text-[10px] text-zinc-400 uppercase font-bold tracking-widest mt-1">
              {provider}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );

  return path ? <Link to={path} className="block h-full">{cardContent}</Link> : cardContent;
};

// ==========================================
// SIGN-IN DIV
// ==========================================
// ⚠️ MODÜL KAPSAMINDA: hem SignInBanner hem Home kullanıyor. Bir bileşenin
// içinde tanımlanırsa diğerinden ERİŞİLEMEZ (tam olarak bu hatayı aldık:
// "Can't find variable: failedAvatars"). Saf fonksiyon, state tutmuyor —
// bileşen içinde durmasının hiçbir gerekçesi yok.
// LiveChat / Header / Dashboard ile AYNI palet ve AYNI formül.
const getAvatarColor = (username) => {
  const colors = [
    'from-purple-500 to-rose-500', 'from-purple-500 to-indigo-500',
    'from-blue-500 to-cyan-500', 'from-green-500 to-green-500',
    'from-orange-500 to-amber-500', 'from-red-500 to-purple-500',
  ];
  return colors[username?.charCodeAt(0) % colors.length || 0];
};

const SignInBanner = ({ isAuthenticated, user }) => {
  const displayName = user?.site_username || user?.username || 'Player';

  // Bu bileşenin KENDİ avatarı için. `failedAvatars` BURADA DEĞİL: bahis
  // listesi ayrı bir bileşende (Home) ve state bileşen sınırını geçemez.
  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => { setAvatarFailed(false); }, [user?.roblox_username]);
  const level = Number(user?.level) || 1;
  const currentXp = Number(user?.xp) || 0;
  const requiredXp = Math.floor(Math.pow(level, 1.9) * 50);
  const progressPct = Math.max(0, Math.min(100, requiredXp > 0 ? (currentXp / requiredXp) * 100 : 0));

  // İlk render'dan ÖNCE karar verilir → Mac'te siyah kutu bir kare bile görünmez.
  const [showChest] = useState(() => supportsAlphaVideo());

  return (
    <div className="relative w-full h-full">
      <div className="absolute inset-0 rounded-2xl overflow-hidden border border-[#2A2D31] bg-[#1A1D20]">
        {/* ⚠️ 0.12 ÇOK SÖNÜKTÜ. Bu desen bannerın kimliği: sağa aşağı akan
            logo şeridi. 0.12'de neredeyse zeminle birleşiyor ve hareketi
            ancak dikkat edince fark ediliyordu — yani animasyonun bedelini
            ödeyip karşılığını alamıyorduk. 0.20 hâlâ arka plan; metnin
            önüne geçmiyor ama desen olarak OKUNUYOR.
            ⚠️ Üstteki soldan sağa koyu geçiş (from-[#1A1D20]) zaten
            başlığın arkasını kapatıyor, bu yüzden okunurluk düşmüyor:
            desen yalnızca sağ tarafta, boş alanda görünür hâle geliyor. */}
        <div className="absolute inset-0 overflow-hidden opacity-[0.20]">
          <div
            className="emerald-loop absolute grid"
            style={{ inset: '-90px', gridTemplateColumns: 'repeat(auto-fill, 62px)', gridAutoRows: '62px', placeItems: 'center' }}
          >
            {Array.from({ length: 300 }).map((_, i) => (
              <img key={i} src="/logo.png" alt="" style={{ width: 46, height: 46, objectFit: 'contain' }} />
            ))}
          </div>
        </div>
        <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 85% 55%, rgba(176,89,232,0.18) 0%, transparent 60%)' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1D20] via-[#1A1D20]/85 to-transparent" />
        <BottomGreenGlow className="z-[4]" />
      </div>

      {/* Karakter videosu: telefonlarda gizli (hidden md:block) + Apple cihazlarda hiç mount
          edilmez (alpha kanallı WebM Safari/macOS'ta siyah kutu olarak çiziliyor). */}
      {showChest && (
        <video
          src="/openChest.webm"
          autoPlay
          loop
          muted
          playsInline
          className="hidden md:block absolute right-1 sm:right-3 -bottom-16 h-[210%] w-auto object-contain object-bottom z-[2] pointer-events-none drop-shadow-[0_15px_30px_rgba(0,0,0,0.6)]"
        />
      )}

      {/* Video yoksa (mobil veya Mac) metin sağdaki boşluğu da kullansın — kırpılmış görünmez. */}
      <div className={`absolute inset-0 z-10 h-full flex flex-col justify-center px-5 sm:px-7 ${showChest ? 'max-w-full md:max-w-[62%]' : 'max-w-full'}`}>
        {isAuthenticated ? (
          <>
            <div className="flex items-center gap-3">
              {/* Kullanıcının profil fotoğrafını çekme işlemi */}
              {/* ⚠️ KOŞUL YANLIŞTI: `site_username` giriş yapmış HERKESTE dolu,
                  yani alttaki dal ÖLÜ KODDU ve Roblox'u olmayan kullanıcı için
                  bile avatar isteniyordu. Doğru koşul: Roblox adı bağlı mı? */}
              {user?.roblox_username && !avatarFailed ? (
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full border-2 border-[#B059E8]/60 overflow-hidden shadow-[0_0_15px_rgba(176,89,232,0.5)] shrink-0">
                  <img
                    src={`${BACKEND_URL}/api/user/avatar/${user.site_username}?v=${encodeURIComponent(user?.roblox_username || '')}`}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={() => setAvatarFailed(true)}
                  />
                </div>
              ) : (
                <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full border-2 border-[#B059E8]/60 flex items-center justify-center shadow-[0_0_15px_rgba(176,89,232,0.5)] shrink-0 bg-gradient-to-br ${getAvatarColor(user?.site_username)}`}>
                  <span className="text-white font-bold text-lg">
                    {user?.site_username?.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              
              <div className="flex flex-col leading-tight min-w-0">
                <span className="text-[#c98bf0] text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em]">Welcome Back</span>
                <span className="text-white font-heading font-black text-lg sm:text-2xl truncate">{displayName}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-2.5 max-w-[280px]">
              <span className="text-zinc-300 text-xs font-black w-3 text-center">{level}</span>
              <div className="flex-1 h-2 rounded-full bg-[#2A2D31] overflow-hidden relative">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#8a2be2] to-[#d29bff] shadow-[0_0_8px_rgba(176,89,232,0.6)] transition-[width] duration-700"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="text-zinc-500 text-xs font-black w-3 text-center">{level + 1}</span>
            </div>

            <Link
              to="/rakeback"
              className="inline-flex items-center justify-center mt-3 px-5 sm:px-7 py-2 sm:py-2.5 rounded-lg text-white font-black text-[11px] sm:text-sm tracking-wide relative overflow-hidden shadow-lg transition-transform hover:scale-[1.03] w-fit"
              style={{ background: '#16A34A' }}
            >
              <span className="relative z-10">View Daily Rewards</span>
            </Link>
          </>
        ) : (
          <>
            <h3 className="font-heading font-black text-white text-sm sm:text-xl xl:text-base 2xl:text-xl leading-snug tracking-wide">
              Register easily with your <span className="text-[#B059E8]">E-mail</span>,{' '}
              to access BloxBets.com
            </h3>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('donut:auth', { detail: { tab: 'register' } }))}
              className="inline-flex items-center gap-2 mt-3 px-8 sm:px-10 py-2.5 sm:py-3 rounded-lg text-white font-black text-xs sm:text-sm uppercase tracking-wider relative overflow-hidden shadow-[0_6px_20px_rgba(176,89,232,0.35)] transition-transform hover:scale-[1.03] w-fit"
              style={{ background: '#B059E8' }}
            >
              <span
                className="absolute inset-0 z-0"
                style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.18) 0px, rgba(0,0,0,0.18) 9px, transparent 9px, transparent 20px)' }}
              />
              <LogIn className="w-4 h-4 sm:w-5 sm:h-5 relative z-10" />
              <span className="relative z-10">Register</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ==========================================
// 3D CAROUSEL BİLEŞENİ
// ==========================================
/* Discord amblemi — resmi vektör.
   ⚠️ BANNER GÖRSELİNDE DE VAR, AMA FARKLI BİR İŞ YAPIYOR. Görseldeki
   amblem ışık huzmesinin içinde, atmosferik ve yumuşak; sahnenin parçası.
   Buradaki ise BAŞLIĞIN YANINDA, keskin ve küçük — kartı okumadan tanıtan
   marka işareti. Karusel her kartı ~10 saniye gösteriyor; göz önce sola,
   başlığa gidiyor ve orada bir tanıma noktası olması gerekiyor.
   ⚠️ Vektör dışarıdan indirilmedi; sitenin kendi kaynağında zaten duran
   yolun aynısı, böylece banner ile buradaki şekil birebir aynı. */
const DiscordMark = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
  </svg>
);

const PromoCarousel = () => {
    const [activeIndex, setActiveIndex] = useState(0);

    const items = [
        {
            id: 'rakeback',
            title: 'REWARDS UPON LEVEL',
            subtitle: 'EVERY SINGLE LEVEL BOOSTS YOUR CASHBACK UP TO 43%',
            path: '/rakeback',
            isExternal: false,
            bg: 'bg-[#17111f]',
            border: 'border-[#B059E8]/30 hover:border-[#B059E8]/50',
            accent: 'text-[#c98bf0]',
            btnBg: 'bg-[#B059E8]/10 text-[#c98bf0] hover:bg-[#B059E8]/20 border-[#B059E8]/30 shadow-[0_0_15px_rgba(176,89,232,0.2)]',
            btnText: 'Claim Rewards',
            image: '/rakebackbanner.webp',
        },
        {
            id: 'discord',
            title: 'JOIN VIP',
            subtitle: 'EXCLUSIVE DISCORD REWARDS',
            path: 'https://discord.gg/Unrq8yfVp6',
            isExternal: true,
            bg: 'bg-[#0f111a]',
            border: 'border-[#5865F2]/20 hover:border-[#5865F2]/40',
            accent: 'text-[#5865F2]',
            btnBg: 'bg-[#5865F2]/10 text-[#5865F2] hover:bg-[#5865F2]/20 border-[#5865F2]/30',
            btnText: 'Join Discord',
            image: '/discordbanner.webp',
            mark: 'discord',
        },
        {
            id: 'affiliate',
            title: 'AFFILIATES',
            subtitle: 'EARN UP TO 10% PASSIVE INCOME FROM EVERY REFERRAL',
            path: '/affiliates',
            isExternal: false,
            bg: 'bg-[#0a1410]',
            border: 'border-[#16A34A]/25 hover:border-[#16A34A]/45',
            accent: 'text-[#2ecf6e]',
            btnBg: 'bg-[#16A34A]/10 text-[#2ecf6e] hover:bg-[#16A34A]/20 border-[#16A34A]/30',
            btnText: 'Create Code',
            image: '/affiliatesbanner.webp',
        }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % items.length);
        }, 10000);
        return () => clearInterval(timer);
    }, [activeIndex]);

    const getStyles = (index) => {
        if (index === activeIndex) {
            return { zIndex: 30, scale: 1, x: "0%", y: "0%", rotateY: 0, rotateZ: 0, opacity: 1, filter: "brightness(1)" };
        }
        if (index === (activeIndex - 1 + items.length) % items.length) {
            return { zIndex: 20, scale: 0.85, x: "-40%", y: "0%", rotateY: 20, rotateZ: -2, opacity: 0.7, filter: "brightness(0.4)" };
        }
        if (index === (activeIndex + 1) % items.length) {
            return { zIndex: 20, scale: 0.85, x: "40%", y: "0%", rotateY: -20, rotateZ: 2, opacity: 0.7, filter: "brightness(0.4)" };
        }
        return { opacity: 0, zIndex: 0, scale: 0.5 };
    };

    return (
        <div className="relative w-full xl:max-w-[460px] 2xl:max-w-[630px] h-[140px] sm:h-[170px] xl:h-[140px] 2xl:h-[170px] flex items-center justify-center overflow-visible" style={{ perspective: '1200px' }}>
            {items.map((item, index) => {
                const isActive = index === activeIndex;

                const cardInner = (
                    <motion.div
                        key={item.id}
                        initial={false}
                        animate={getStyles(index)}
                        transition={{ duration: 0.7, type: "spring", bounce: 0.2 }}
                        className="absolute w-full max-w-[280px] sm:max-w-[380px] xl:max-w-[280px] 2xl:max-w-[380px] h-[140px] sm:h-[170px] xl:h-[140px] 2xl:h-[170px] cursor-pointer group"
                        onClick={(e) => {
                            if (!isActive) {
                                e.preventDefault();
                                setActiveIndex(index);
                            }
                        }}
                    >
                        <div className={`w-full h-full rounded-2xl ${item.bg} border ${item.border} p-5 sm:p-6 xl:p-4 2xl:p-6 flex flex-col justify-center relative overflow-hidden shadow-2xl transition-all`}>
                            {/* ══════════════════════════════════════════════
                                ARKA PLAN GÖRSELİ
                                ══════════════════════════════════════════════
                                ⚠️ ESKİ KATMANLAR SÖKÜLDÜ: nokta deseni,
                                radyal `item.glow` ve sağ alttaki dev ikon.
                                Görselin kendisi zaten ışığını ve konusunu
                                taşıyor; üçü üstüne binince kart karman
                                çorman oluyordu.

                                ⚠️ `object-right`: görselin oranı 2,233,
                                kartınki geniş ekranda 2,235 — neredeyse
                                aynı, kırpma yok. Ama küçük kırılımda kart
                                280×140 (oran 2,0) oluyor ve `object-cover`
                                yatayda kırpmak zorunda kalıyor. Sağa
                                sabitlemek kırpmayı SOL taraftan yaptırıyor;
                                orası zaten bilerek boş bırakıldı, sağdaki
                                asıl görsel korunuyor.

                                ⚠️ `group-hover:scale-105`: ikonun hover
                                animasyonu gitti, yerine görselin çok hafif
                                yaklaşması geldi. Kart tıklanabilir ve bunu
                                söyleyen bir tepki kalmalı. */}
                            <img
                                src={item.image}
                                alt=""
                                aria-hidden
                                loading="lazy"
                                draggable={false}
                                className="absolute inset-0 w-full h-full object-cover object-right
                                           pointer-events-none select-none
                                           transition-transform duration-700 group-hover:scale-105"
                            />

                            {/* ⚠️ PERDE ŞART. Görselin sol tarafı karanlık
                                üretildi ama buna GÜVENMİYORUZ: bir gün başka
                                bir görsel konur ya da mevcut biri değişirse
                                başlık okunmaz olur. Perde soldan sağa açılıyor
                                ve %85'te tamamen bitiyor — metnin durduğu
                                bölgeyi kapatıyor, sağdaki görseli açıkta
                                bırakıyor. */}
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{ background: 'linear-gradient(90deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.74) 32%, rgba(0,0,0,0.38) 60%, rgba(0,0,0,0) 85%)' }}
                            />

                              <div className="relative z-10 flex flex-col items-start w-[85%] sm:w-[75%] h-full">
                                <h3 className="font-heading font-black text-lg sm:text-2xl xl:text-lg 2xl:text-2xl leading-tight sm:leading-none text-white uppercase tracking-wider drop-shadow-md flex items-center gap-2">
                                    {item.mark === 'discord' && (
                                        <DiscordMark className="w-5 h-5 sm:w-6 sm:h-6 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 text-[#5865F2] shrink-0 drop-shadow-[0_0_8px_rgba(88,101,242,0.7)]" />
                                    )}
                                    {item.title}
                                </h3>
                                <p className={`font-bold text-[8px] sm:text-[11px] xl:text-[8px] 2xl:text-[11px] uppercase tracking-widest mt-1 sm:mt-1.5 mb-auto line-clamp-2 ${item.accent}`}>
                                    {item.subtitle}
                                </p>

                                {isActive && (
                                    <button className={`mt-auto px-3 py-1.5 sm:px-5 sm:py-2 xl:px-3 xl:py-1.5 2xl:px-5 2xl:py-2 rounded-lg text-[9px] sm:text-[11px] xl:text-[9px] 2xl:text-[11px] font-black uppercase tracking-widest transition-all border whitespace-nowrap ${item.btnBg}`}>
                                        {item.btnText}
                                    </button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                );

                if (isActive) {
                    return item.isExternal ? (
                        <a href={item.path} target="_blank" rel="noopener noreferrer" key={item.id} className="absolute inset-0 flex items-center justify-center">
                            {cardInner}
                        </a>
                    ) : (
                        <Link to={item.path} key={item.id} className="absolute inset-0 flex items-center justify-center">
                            {cardInner}
                        </Link>
                    );
                }

                return cardInner;
            })}

            <div className="absolute -bottom-5 flex gap-2 z-40">
                {items.map((_, idx) => (
                    <div
                        key={idx}
                        onClick={() => setActiveIndex(idx)}
                        className={`h-1 rounded-full cursor-pointer overflow-hidden transition-all duration-500 bg-white/10`}
                        style={{ width: idx === activeIndex ? '2.5rem' : '0.5rem' }}
                    >
                        {idx === activeIndex && (
                            <motion.div
                                key={`progress-${activeIndex}`}
                                initial={{ width: "0%" }}
                                animate={{ width: "100%" }}
                                transition={{ duration: 10, ease: "linear" }}
                                className="h-full bg-white/80"
                            />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export const Home = () => {
  // Avatarı yüklenemeyen kullanıcılar (canlı bahis listesi). Kullanıcı başına
  // tutulur: aynı kişi listede birden çok kez varsa tekrar denemenin anlamı yok.
  const [failedAvatars, setFailedAvatars] = useState(() => new Set());
  const { isAuthenticated, user } = useAuth();
  const [liveBets, setLiveBets] = useState([]);
  const [isMaintenance, setIsMaintenance] = useState(false);
  
  // Chat varsayılan durumunu ekrana göre belirliyoruz
  // 💬 Aç/kapa durumu paylaşımlı.
  const { chatOpen } = useChatLayout();

  // 💬 Mobilde kapalı başlatma ve Header'ın 'toggleMobileChat' sinyalini
  // dinleme artık ChatLayoutProvider'da — böylece bu davranışlar YALNIZCA
  // ana sayfada değil, TÜM sayfalarda geçerli.

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === 503 && error.response.data?.maintenance) {
          setIsMaintenance(true);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    if (isMaintenance) {
      const pingTimer = setInterval(async () => {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/health`);
          if (res.status === 200) {
            setIsMaintenance(false);
            window.location.reload();
          }
        } catch (error) {}
      }, 10000);
      return () => clearInterval(pingTimer);
    }
  }, [isMaintenance]);

  // 🚀 CANLI AKIŞ — sekme ARKA PLANDAYKEN poll DURUR
  // ESKİDEN: kullanıcı başka sekmeye geçse bile 3 sn'de bir istek atmaya devam ediyordu.
  // Açık unutulan sekmeler saatlerce boşuna sunucuya yükleniyordu.
  // Artık sadece sekme GÖRÜNÜRKEN poll yapılıyor; geri dönüldüğünde ANINDA tazeleniyor.
  //
  // NOT: Backend'de /game/live artık 2 sn TTL cache + thundering-herd kilidi ile korunuyor.
  //      Kaç kişi aynı anda çekerse çeksin 2 sn'de en fazla 1 Mongo sorgusu yapılır.
  useEffect(() => {
    if (isMaintenance) return;

    let interval = null;

    const fetchLiveBets = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/game/live`);
        setLiveBets(res.data);
      } catch (error) {}
    };

    const start = () => {
      if (interval) return;            // zaten çalışıyor -> ikinci interval kurma
      fetchLiveBets();                 // sekmeye dönünce anında tazele
      interval = setInterval(fetchLiveBets, 3000);
    };

    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    handleVisibility();                // ilk yüklemede sekme görünürse başlat
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      stop();
    };
  }, [isMaintenance]);

  /* ⚠️ SIRA ÖNEMLİ: dikeyler 12'lik ızgarada CASE BATTLES'ın (6 sütun)
     yanına 2'şer sütunla diziliyor — 6+2+2+2 = 12. MM2 ilk sırada,
     yani doğrudan CASE BATTLES'ın yanında. */
  const featuredGames = [
    { name: 'CASE BATTLES', provider: 'BloxBets Original', isHot: true, path: '/battles',      video: '/casebattlesVideo.mp4',  shape: 'horizontal' },
    { name: 'MM2 COINFLIP', provider: 'BloxBets Original', isHot: true, path: '/mm2game',      video: '/mm2gamebanner.mp4',    shape: 'vertical',
      /* Dar kartta uzun metin taşıyor; kırılıma göre biri gösteriliyor. */
      tagline: { short: 'MM2 items!', full: 'Play with MM2 items!' } },
    { name: 'BLACKJACK',    provider: 'BloxBets Original',                path: '/blackjack',    video: '/blackjackVideo.mp4',   shape: 'vertical' },
    { name: 'CHICKEN ROAD', provider: 'BloxBets Original',                path: '/chicken-road', video: '/chickenRoadVideo.mp4', shape: 'vertical' },
  ];

  /* CRASH dikeylerden buraya taşındı — 9 kutu tek satıra sığsın diye
     ızgara xl'de 9 sütuna çıkarıldı. */
  const squareGames = [
    { name: 'CRASH',    provider: '', path: '/crash',    video: '/crashVideo.mp4',    shape: 'square' },
    { name: 'ROULETTE', provider: '', path: '/roulette', video: '/rouletteVideo.mp4', shape: 'square' },
    { name: 'TOWERS',   provider: '', path: '/tower',    video: '/towersVideo.mp4',   shape: 'square' },
    { name: 'PLINKO',   provider: '', path: '/plinko',   video: '/plinkoVideo.mp4',   shape: 'square' },
    { name: 'DICE',     provider: '', path: '/dice',     video: '/diceVideo.mp4',     shape: 'square' },
    { name: 'MINES',    provider: '', path: '/mines',    video: '/minesVideo.mp4',    shape: 'square' },
    { name: 'CASES',    provider: '', path: '/cases',    video: '/casesVideo.mp4',    shape: 'square' },
    { name: 'JACKPOT',  provider: '', path: '/jackpot',  video: '/jackpotVideo.mp4',  shape: 'square' },
    { name: 'COINFLIP', provider: '', path: '/coinflip', video: '/coinflipVideo.mp4', shape: 'square' },
  ];

  if (isMaintenance) {
    return (
        <div className="min-h-screen bg-[#0E0F11] flex flex-col items-center justify-center p-6 text-center relative overflow-hidden z-[100]">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#B059E8]/10 blur-[120px] pointer-events-none rounded-full" />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="relative z-10 flex flex-col items-center">
                <div className="w-24 h-24 bg-[#1A1D20] rounded-full border-2 border-[#2A2D31] flex items-center justify-center mb-8 shadow-[0_0_30px_rgba(176,89,232,0.3)] relative">
                    <Settings className="w-12 h-12 text-[#B059E8] animate-spin" style={{ animationDuration: '3s' }} />
                </div>
                <h1 className="font-heading font-black text-4xl md:text-6xl text-white uppercase tracking-widest mb-4 drop-shadow-lg">
                    Under <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B059E8] to-[#16A34A]">Maintenance</span>
                </h1>
                <p className="text-zinc-400 text-lg md:text-xl max-w-2xl leading-relaxed mb-10">
                    We are currently upgrading our servers. Please check back shortly!
                </p>
            </motion.div>
        </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0E0F11] relative font-sans" data-testid="home-page">
      <style>{`
        @keyframes emerald-scroll { from { transform: translate(0, 0); } to { transform: translate(62px, 62px); } }
        /* ⚠️ 20s → 16s. 62px'lik döngü mesafesi sabit olduğu için süreyi
           kısaltmak hızı artırıyor: 3,1 px/sn → 3,9 px/sn. Bilerek az —
           bu bir arka plan dokusu, gözü metinden çekmemeli.
           ⚠️ Bu süre index.css'teki reduced-motion muafiyetinde de yazılı;
           birini değiştirirken ötekini de güncelleyin. */
        .emerald-loop { animation: emerald-scroll 16s linear infinite; }
        html { scrollbar-width: thin; scrollbar-color: #3a3d43 #0E0F11; }
        ::-webkit-scrollbar { width: 10px; height: 10px; }
        ::-webkit-scrollbar-track { background: #0E0F11; }
        ::-webkit-scrollbar-thumb { background: linear-gradient(180deg, #B059E8 0%, #7b2fc4 100%); border-radius: 8px; border: 2px solid #0E0F11; }
        ::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, #c98bf0 0%, #B059E8 100%); }
      `}</style>

      {/* Arkaplan banner'ı kaldırıldı: artık App.js'teki GLOBAL arkaplan kullanılıyor
          (banner + koyulaştırma + yeşil ışıltı). Böylece Home da diğer sayfalarla aynı görünür. */}

      {/* 💬 Sohbet App.js'te TEK kopya (ChatDock) — mobil kaplama
          dahil tüm davranış oraya taşındı. */}
      {/* ══════════════════════════════════════════════════════════════════
          ANA İÇERİK
          ══════════════════════════════════════════════════════════════════
          ⚠️ SOHBET BOŞLUĞU İÇ KABA TAŞINDI. Önceden `md:pl-[370px]` DIŞ
          kapta, `px-8` ise İÇ kaptaydı. İkisi ayrı elemanda olunca dolgular
          TOPLANIYOR ve içerik 370+32 = 402px'ten başlıyordu. Sitedeki diğer
          26 sayfanın hepsi ikisini AYNI elemanda tutuyor; Tailwind'de `pl-`
          kuralı `px-`'in solunu ezdiği için orada içerik tam 370px'ten
          başlıyor. Yani Home, sohbetin sağında 32px fazladan boşluk
          bırakan tek sayfaydı.

          ⚠️ ÜST BOŞLUK md:pt-28 → md:pt-24. Header 80px (h-20). pt-28
          (112px) header'ın altında 32px boşluk bırakıyordu; sitedeki diğer
          sayfalar pt-24 (96px) ile 16px bırakıyor. Home yine tek başına
          iki katıydı.
          ══════════════════════════════════════════════════════════════════ */}
      <div className="relative z-10">
        <div className={`max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-24 pb-12
                         transition-[padding] duration-300
                         ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>
          <main className="flex flex-col gap-6">

            {/* ══════════════════════════════════════════════════════════════
                BAŞLIK
                ⚠️ ESKİ HÂLİ "THE ONLY FAIR ROBLOX CASINO - BloxBets.com" İDİ
                ve iki şeyi birden yanlış yapıyordu: (1) site bir Roblox
                kumarhanesi değil, MM2 kumarhanesi — bütün ekonomi bıçak ve
                silah üzerine kurulu; (2) marka adı H1'in içine sıkışınca
                başlık iki cümleye dönüşüyor ve göz nereye bakacağını
                bilemiyordu. Marka artık üst etikette, başlık tek bir iddia.

                ⚠️ "ONLY FAIR" → "MOST REWARDING". İlki savunmacı bir iddiaydı
                (rakiplerin hileli olduğunu ima ediyor) ve zaten üst etikette
                "Provably fair" yazdığı için kendini tekrar ediyordu. İkincisi
                oyuncuya ne KAZANACAĞINI söylüyor; adalet iddiası da
                kaybolmadı, sadece doğru yere taşındı.
                ══════════════════════════════════════════════════════════════ */}
            <header className="flex flex-col mb-1">
                <div className="flex items-center gap-2.5 mb-3">
                  <Mm2Logo size={18} />
                  <span className="mm-eyebrow">BloxBets · Provably fair</span>
                  {/* Kılcal çizgi başlığın genişliğini duyurur — etiketi
                      havada bırakmaz. */}
                  <span aria-hidden className="h-px flex-1 bg-gradient-to-r from-[#2A2D31] to-transparent" />
                </div>
                <h1 className="font-heading font-black text-[26px] sm:text-4xl md:text-[2.75rem] text-white tracking-wide leading-[1.05]">
                  THE MOST REWARDING{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#c98bf0] via-[#B059E8] to-[#2ecf6e]">
                    MURDER MYSTERY 2
                  </span>{' '}
                  CASINO
                </h1>
                {/* ⚠️ `max-w-2xl` KALDIRILDI. 42rem'lik tavan yüzünden cümle,
                    yanında bomboş yer varken ikinci satıra atlıyordu —
                    okuma genişliğini korumak için konmuştu ama burada
                    koruyacak bir şey yoktu: bu bir paragraf değil, tek
                    cümlelik bir alt başlık. Artık kabın genişliğini
                    kullanıyor ve geniş ekranda tek satırda kalıyor. */}
                <p className="text-zinc-400 text-sm mt-2.5 font-medium leading-relaxed">
                  Trade your MM2 items to our bots, play, trade them back out.
                  Every roll is provably fair — just for fun, no real-world value.
                </p>
            </header>

            <section className="flex flex-col xl:flex-row gap-4">
                <div className="w-full xl:w-5/12 h-[150px] sm:h-[180px] xl:h-[170px] 2xl:h-[180px]">
                    <SignInBanner isAuthenticated={isAuthenticated} user={user} />
                </div>

                <div className="w-full xl:w-7/12 h-[150px] sm:h-[180px] xl:h-[170px] 2xl:h-[180px] relative flex items-center justify-center xl:justify-end">
                    <PromoCarousel />
                </div>
            </section>

            <section data-testid="games-section" className="mt-4">
              {/* ⚠️ BÖLÜM BAŞLIĞI DEVİCE'I DEĞİŞTİ. Eskiden renkli bir hap +
                  başlık + açıklama vardı; hap hiçbir şey söylemiyordu, sadece
                  renk koyuyordu. Şimdi başlığın sağından çıkan kılcal çizgi
                  bölümün nereye kadar uzandığını GÖSTERİYOR ve sayım
                  (12 games) gerçek bir bilgi taşıyor. */}
              <div className="flex items-center gap-3 mb-4">
                <h2 className="font-heading font-black text-lg sm:text-xl text-white uppercase tracking-wider shrink-0">
                  Originals
                </h2>
                <span aria-hidden className="h-px flex-1 bg-gradient-to-r from-[#2A2D31] via-[#2A2D31] to-transparent" />
                <span className="mm-eyebrow shrink-0">
                  {featuredGames.length + squareGames.length} games
                </span>
              </div>

              {/* ⚠️ pt: MM2 kartının üstündeki sekme kartın DIŞINDA duruyor.
                  Boşluk olmasa yukarıdaki başlığın üstüne binerdi. */}
              <div className="grid grid-cols-3 lg:grid-cols-12 gap-3 sm:gap-4 pt-8 sm:pt-10">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0 }}
                  className="col-span-3 lg:col-span-6 h-[150px] sm:h-[200px] lg:h-[260px]"
                >
                  <GameCard {...featuredGames[0]} />
                </motion.div>

                {featuredGames.slice(1).map((game, i) => (
                  <motion.div
                    key={game.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (i + 1) * 0.05 }}
                    className="col-span-1 lg:col-span-2 h-[170px] sm:h-[230px] lg:h-[260px]"
                  >
                    <GameCard {...game} />
                  </motion.div>
                ))}
              </div>

              {/* ⚠️ 9 KUTU: eskiden 8 idi ve 8 sütunda tam sığıyordu. CRASH
                  eklenince 8'lik ızgarada tek başına ikinci satıra düşüyordu.
                  Sütun sayısı 9'a çıkarıldı — kutular biraz küçülüyor ama
                  hepsi tek satırda kalıyor. Ara kırılımlar da 9'un bölenlerine
                  göre seçildi (3 ve 5) ki hiçbir satır tek kutuyla bitmesin. */}
              <div className="grid grid-cols-3 sm:grid-cols-5 xl:grid-cols-9 gap-2.5 sm:gap-3 mt-3 sm:mt-4">
                {squareGames.map((game, index) => (
                  <motion.div
                    key={game.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.04 }}
                    className="aspect-square"
                  >
                    <GameCard {...game} />
                  </motion.div>
                ))}
              </div>
            </section>

            <section className="mt-6" data-testid="live-bets-section">
              <div className="flex items-center gap-3 mb-4">
                <h2 className="font-heading font-black text-lg sm:text-xl text-white uppercase tracking-wider shrink-0">
                  Live bets
                </h2>
                <span aria-hidden className="h-px flex-1 bg-gradient-to-r from-[#2A2D31] via-[#2A2D31] to-transparent" />
                {/* Nabız YALNIZCA gerçekten akış varken atar. Boş tabloda
                    yanıp sönen yeşil nokta, olmayan bir hareketi vaat eder. */}
                <span className="mm-eyebrow shrink-0 inline-flex items-center gap-1.5">
                  {liveBets.length > 0 && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2ecf6e] animate-pulse" />
                  )}
                  {liveBets.length > 0 ? 'Streaming' : 'Idle'}
                </span>
              </div>

              <div className="w-full mm-panel overflow-hidden relative">
                <div className="overflow-x-auto scrollbar-none">
                  <table className="w-full text-left border-collapse min-w-[600px]">
                    <thead>
                      <tr className="bg-[#121417] text-zinc-500 text-[10px] uppercase tracking-[0.16em] font-bold border-b border-[#2A2D31]">
                        <th className="py-4 px-6 font-bold">Game</th>
                        <th className="py-4 px-6 font-bold">Player</th>
                        <th className="py-4 px-6 font-bold text-right">Bet</th>
                        <th className="py-4 px-6 font-bold text-center">Multiplier</th>
                        <th className="py-4 px-6 font-bold text-right">Payout</th>
                      </tr>
                    </thead>
                    <tbody className="text-xs font-medium">
                      <AnimatePresence>
                        {liveBets.map((bet, index) => {
                          const isWin = bet.win_amount > 0;
                          return (
                            <motion.tr
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0 }}
                              key={bet.id}
                              className={`hover:bg-white/5 transition-colors group ${index !== liveBets.length - 1 ? 'border-b border-[#2A2D31]/60' : ''}`}
                            >
                              <td className="py-3 px-6 text-zinc-300 font-bold capitalize flex items-center gap-2.5">
                                 <Gamepad2 className="w-4 h-4 text-zinc-500 group-hover:text-[#B059E8] transition-colors" />
                                 {bet.game_type}
                              </td>
                                <td className="py-3 px-6">
                                 <div className="flex items-center gap-2.5">
                                   {/* ⚠️ `has_mc_account` diye bir alan backend'de
                                       HİÇ YOK — /game/live `has_roblox_account`
                                       gönderiyor. Alan hep undefined olduğu için
                                       bu listede avatar HİÇBİR ZAMAN görünmüyordu. */}
                                   {bet.has_roblox_account && !failedAvatars.has(bet.username) ? (
                                     <img
                                      src={`${BACKEND_URL}/api/user/avatar/${bet.username}`}
                                      alt="avatar"
                                      className="w-6 h-6 shrink-0 rounded-md border border-zinc-700 object-cover shadow-sm"
                                      onError={() => setFailedAvatars(prev => {
                                        if (prev.has(bet.username)) return prev;
                                        const next = new Set(prev);
                                        next.add(bet.username);
                                        return next;
                                      })}
                                     />
                                   ) : (
                                     <div className={`w-6 h-6 shrink-0 rounded-md border border-zinc-700 flex items-center justify-center text-[10px] text-white font-bold bg-gradient-to-br ${getAvatarColor(bet.username)}`}>
                                       {bet.username?.charAt(0).toUpperCase()}
                                     </div>
                                   )}
                                   <span className="text-zinc-200 truncate max-w-[120px] font-medium">{bet.username}</span>
                                 </div>
                              </td>
                              <td className="py-3 px-6 text-right mm-num text-zinc-400 text-[12px]">
                                {/* ⚠️ Canlı akışta İKİ CÜZDANIN oyunları yan yana görünür;
                                    backend her satıra `wallet` mühürlüyor. */}
                                <WalletValue amount={bet.bet_amount} wallet={bet.wallet} />
                              </td>
                              <td className="py-3 px-6 text-center mm-num">
                                <span className={`px-3 py-1 rounded-full font-black text-[10px] tracking-wider ${isWin ? 'bg-[#16A34A]/10 text-[#2ecf6e] border border-[#16A34A]/20 shadow-[0_0_10px_rgba(22,163,74,0.1)]' : 'bg-zinc-800 text-zinc-400 border border-zinc-700/50'}`}>
                                  {bet.multiplier.toFixed(2)}x
                               </span>
                              </td>
                              <td className={`py-3 px-6 text-right mm-num font-bold ${isWin ? 'text-[#2ecf6e] drop-shadow-[0_0_8px_rgba(46,207,110,0.3)]' : 'text-zinc-500'}`}>
                                {isWin ? `+ ${walletAmount(bet.win_amount, bet.wallet)}` : `- ${walletAmount(bet.bet_amount, bet.wallet)}`}
                              </td>
                            </motion.tr>
                          );
                        })}
                      </AnimatePresence>
                      {liveBets.length === 0 && (
                        <tr>
                          <td colSpan="5" className="py-8 text-center text-zinc-600 flex flex-col items-center gap-3 justify-center text-xs font-medium">
                            <Activity className="w-6 h-6 animate-spin opacity-50" />
                            Waiting for live bets...
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

          </main>
        </div>
      </div>

    </div>
  );
};